/*
 * o16-pdp-title-fit.js
 * Loaded by sections/main-product.liquid via a deferred <script> tag.
 *
 * FIT-TO-ONE-LINE for the PDP product title (.product__title — the <h1>
 * rendered by snippets/main-product-section.liquid, or its aria-hidden <span>
 * mirror in the secondary render, see DUAL RENDER below).
 *
 * GOVERNING RULE (owner 2026-07-09): on mobile, the product title must NEVER
 * wrap to two lines, however long the title string is. white-space:nowrap in
 * snippets/o16-pdp-styles.liquid guarantees no wrap; this routine guarantees
 * the FULL title still FITS on that one line by shrinking font-size — from
 * its natural (settings-driven --font-heading-h1-scale) size down to a
 * legible floor — only as far as needed. Short titles are left at full size.
 *
 * SCOPE: mobile only (<=749px, matching section-main-product.css's 750px
 * breakpoint between .main-product-desktop-section-wrapper and
 * .main-product-mobile-section-wrapper). The desktop title's size/behavior
 * is never touched.
 *
 * DUAL RENDER: main-product.liquid renders snippets/main-product-section.liquid
 * TWICE when gallery_type != mobile_gallery_type — once for desktop (the real
 * <h1>) inside .main-product-desktop-section-wrapper, and once for mobile
 * (is_secondary:true, an aria-hidden <span> mirror so the page still has
 * exactly one true <h1>) inside .main-product-mobile-section-wrapper. Both
 * copies carry the same .product__title class. CSS media queries make
 * exactly one of the two visible at a time (display:none on the other), so
 * we only fit whichever copy is actually laid out (offsetParent !== null) —
 * this also self-corrects if the editor ever sets gallery_type ==
 * mobile_gallery_type ("synchronize"), which renders a single undivided copy.
 *
 * Pattern reused from assets/o16-header-taglines.js's fit-to-one-line
 * routine: reset to the natural CSS-declared size, measure scrollWidth vs
 * clientWidth, shrink proportionally only if it overflows, clamp to a floor.
 * Verified empirically against the live theme (scrollWidth grows with
 * overflowing nowrap content while clientWidth stays pinned to the real
 * available column width — no flex min-width:0 workaround needed here).
 *
 * Vanilla JS, no dependencies. Token-driven CSS is never altered here beyond
 * the per-element font-size this routine computes.
 */
(function () {
  var SAFETY = 0.98;
  var MIN_PX = 20; /* legible floor — still reads as a heading, not body text */
  var RESIZE_DEBOUNCE_MS = 150;
  var MOBILE_QUERY = '(max-width: 749px)';

  if (!document.querySelector('.product__title')) return; /* no PDP title here */

  /* Re-queried on every pass rather than captured once. The bundle ladder's
     page takeover replaces this section's markup, so the title that needs
     fitting after a swap is a DIFFERENT element from the one on the page at
     load — a cached NodeList would keep measuring an element that no longer
     exists and leave the new title at its full size. That is exactly what
     happened: "THE COLLECTION" stayed at 44px and ran 8px past the right edge
     at 375px, while a normal load of the same page fitted it to 39.8px. */
  function titleEls() {
    return document.querySelectorAll('.product__title');
  }

  var retryFrame = null;

  function isMobile() {
    return window.matchMedia(MOBILE_QUERY).matches;
  }

  /*
   * fitTitle — shrink one .product__title to fit its available width on a
   * single line. Resets to the natural (CSS/settings-driven) size first so
   * it can also GROW back when it has more room (e.g. after a resize) — it
   * only ends up smaller than natural if it still overflows at that size.
   * Skips elements that are not currently laid out (offsetParent === null —
   * the desktop copy while on a mobile viewport, or vice versa) so an
   * invisible instance never receives a stray inline style.
   * Returns false if measurements are not ready yet, so the caller can retry.
   */
  function fitTitle(el) {
    if (el.offsetParent === null) return true; /* not visible — nothing to fit */
    if (el.textContent.trim() === '') return true;

    el.style.fontSize = ''; /* read the natural CSS-declared size */
    var natural = parseFloat(window.getComputedStyle(el).fontSize);
    if (!natural) return false;

    el.style.whiteSpace = 'nowrap'; /* CSS already sets this at <=749px; explicit for the measurement itself */
    el.style.fontSize = natural + 'px';

    var textWidth = el.scrollWidth;
    var available = el.clientWidth;
    if (textWidth <= 0 || available <= 0) return false;

    if (textWidth > available) {
      var fitted = natural * (available / textWidth) * SAFETY;
      fitted = Math.max(MIN_PX, Math.min(natural, fitted));
      el.style.fontSize = fitted + 'px';
    }
    return true;
  }

  /* Undo any inline sizing so the title is governed purely by CSS — used
     when the viewport is desktop-width, so this routine never touches the
     desktop title's size/behavior. */
  function resetTitle(el) {
    el.style.whiteSpace = '';
    el.style.fontSize = '';
  }

  function fitAll() {
    var titles = titleEls();
    if (!isMobile()) {
      titles.forEach(resetTitle);
      return true;
    }
    var allOk = true;
    titles.forEach(function (el) {
      if (!fitTitle(el)) allOk = false;
    });
    return allOk;
  }

  /* Retry via rAF until a pass measures cleanly (guards the rare case where
     layout is not settled yet on the first attempt). */
  function fitAllWithRetry() {
    if (retryFrame) window.cancelAnimationFrame(retryFrame);
    var ok = fitAll();
    if (!ok) {
      retryFrame = window.requestAnimationFrame(fitAllWithRetry);
    }
  }

  var resizeTimer = null;
  function onResize() {
    if (resizeTimer) window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(fitAllWithRetry, RESIZE_DEBOUNCE_MS);
  }

  function bootstrap() {
    fitAllWithRetry(); /* first pass — layout may not be settled yet */

    /* window load: after images / web fonts / external resources settle */
    window.addEventListener('load', fitAllWithRetry, { once: true });

    /* document.fonts.ready: font-swap changes text width, re-measure */
    if (document.fonts && typeof document.fonts.ready === 'object' &&
        typeof document.fonts.ready.then === 'function') {
      document.fonts.ready.then(fitAllWithRetry);
    }

    /* Resize / orientation change re-derives the fitted size, and also
       handles crossing the 750px breakpoint (reset <-> fit). */
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
  }

  /* Exposed so anything that replaces the title can have it measured again.
     The bundle ladder's page takeover swaps this section's markup wholesale,
     and a title that was only ever fitted on DOMContentLoaded came back at its
     default size — "THE COLLECTION" then ran 8px past the viewport at 375px,
     which is a real sideways wobble, not a rounding artefact. fitAllWithRetry
     re-measures from scratch, so calling it again is safe. */
  window.O16PdpTitleFit = { refit: fitAllWithRetry };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap, { once: true });
  } else {
    bootstrap();
  }
})();
